import type { ReactNode } from 'react';

/**
 * Render inline-markdown del manuale DWR su un nodo React.
 *
 * Conversioni supportate (sintassi inline `*...*` come da manuale V1):
 *  - `**testo**` → `<strong>testo</strong>` (grassetto)
 *  - `*testo*`   → `<em>testo</em>`         (corsivo)
 *
 * Non supporta blocchi (heading / liste / tabelle / code-fence): solo
 * sintassi inline. Per blocchi complessi vivono già nello schema DWR
 * come campi strutturati (es. `choiceGroups`, `narrativeFields`).
 *
 * Implementazione **safe** (no `dangerouslySetInnerHTML`): produce un
 * array di `ReactNode` tramite split su regex, evitando XSS anche se
 * un'eventuale fonte testuale fosse esterna in futuro.
 *
 * Edge cases:
 *  - Asterisco singolo isolato (es. "Liv. *") → render letterale (regex
 *    richiede `[^*]+` tra i delimitatori).
 *  - Annidamento (`***testo***`) → NON supportato; il match più lungo
 *    `**` è preferito, l'altro `*` resta letterale.
 */
export function renderInlineMarkdown(text: string | undefined | null): ReactNode {
  if (text === undefined || text === null || text === '') return text ?? '';
  const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return tokens.map((tok, i) => {
    if (tok.startsWith('**') && tok.endsWith('**') && tok.length >= 4) {
      return <strong key={i}>{tok.slice(2, -2)}</strong>;
    }
    if (tok.startsWith('*') && tok.endsWith('*') && tok.length >= 3) {
      return <em key={i}>{tok.slice(1, -1)}</em>;
    }
    return tok;
  });
}
